import dayjs from 'dayjs'
import isNil from 'lodash/isNil'
import type {
  HubspotSyncNewItemDto,
  HubspotSyncSubmitFailureDto,
  HubspotSyncUpdateItemDto,
  HubspotSyncUpdatedItemDto,
  HubspotSyncWarningItemDto,
} from '../../api/dto/client-billing-profile.dto'
import { DISPLAY_DATE_FORMAT, ISO_DATE_FORMAT, PaymentType } from './types'

export type SyncPhase = 'idle' | 'running' | 'ready'

/** The three things a finished sync can leave for the operator to decide. */
export type SyncTaskKey = 'new' | 'changes' | 'review' | 'unsynced'

export const BILLING_PAYMENT_TYPE_OPTIONS = [
  { value: PaymentType.WIRE, label: 'Wire' },
  { value: PaymentType.STRIPE, label: 'Stripe' },
]

export const paymentTypeLabel = (value: string | null): string => {
  if (isNil(value) || value === '') return '—'
  return (
    BILLING_PAYMENT_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value
  )
}

/** Plan start for display. Kept as-is when it is not the ISO date we expect. */
export const planStartLabel = (value: string | null): string => {
  if (isNil(value) || value === '') return '—'
  const parsed = dayjs(value, ISO_DATE_FORMAT, true)
  return parsed.isValid() ? parsed.format(DISPLAY_DATE_FORMAT) : value
}

/** Shown one after another while the run is in flight. */
export const SYNCING_STEPS = [
  'Connecting to HubSpot',
  'Fetching company records',
  'Matching billing profiles',
  'Finalizing',
] as const

export const warningReasonText = (
  reason: HubspotSyncWarningItemDto['reason']
): string =>
  reason === 'duplicate_client_public_key'
    ? 'Points at a SureCo client another HubSpot company already claims'
    : 'Points at a SureCo client that does not exist'

export const isSubmittableNewItem = (item: HubspotSyncNewItemDto): boolean =>
  !isNil(item.clientPublicKey)

const lowered = (emails: string[]): Set<string> =>
  new Set(emails.map((email) => email.toLowerCase()))

/** Case-insensitive dedupe that keeps the first spelling of each address. */
export const dedupeEmails = (emails: string[]): string[] => {
  const seen = new Set<string>()
  return emails.filter((email) => {
    const key = email.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * The profile's recipients as they stand right now. A preview's `toEmails` is
 * the HubSpot set, so the stored list is that minus what HubSpot would add,
 * plus what HubSpot dropped.
 */
/**
 * The addresses a grant request carries: one per person, spelled the way the
 * server reports it back, so a retry can match the failures.
 */
export const grantEmails = (emails: string[]): string[] =>
  Array.from(new Set(emails.map((email) => email.trim().toLowerCase())))

export const currentRecipients = (
  item: HubspotSyncUpdatedItemDto
): string[] => {
  const added = lowered(item.addedEmails)
  return dedupeEmails([
    ...item.toEmails.filter((email) => !added.has(email.toLowerCase())),
    ...item.removedEmails,
  ])
}

export const hasPaymentTypeChange = (
  item: HubspotSyncUpdatedItemDto
): boolean =>
  !isNil(item.proposedPaymentType) &&
  item.proposedPaymentType !== item.currentPaymentType

/** HubSpot owns plan start, so a proposal only counts when it moves the stored value. */
export const hasPlanStartChange = (item: HubspotSyncUpdatedItemDto): boolean =>
  !isNil(item.proposedPlanEffectiveDate) &&
  item.proposedPlanEffectiveDate !== item.currentPlanEffectiveDate

/**
 * A matched profile with something to apply: recipients, a payment type, a plan
 * start, or any combination. A plan start moving on its own still has to reach
 * the operator, otherwise the deal's new plan year is silently dropped.
 */
export const hasPendingAdditions = (item: HubspotSyncUpdatedItemDto): boolean =>
  item.addedEmails.length > 0 ||
  hasPaymentTypeChange(item) ||
  hasPlanStartChange(item)

/** One recipient HubSpot no longer lists, decided on its own row. */
export interface ReviewRecipientRow {
  key: string
  clientBillingProfileId: number
  clientPublicKey: string
  companyName: string
  email: string
}

/** A HubSpot company we could not act on. Read-only: it carries no recipient. */
export interface ReviewCompanyRow {
  key: string
  companyName: string
  reason: string
}

export const buildReviewRecipientRows = (
  changes: HubspotSyncUpdatedItemDto[]
): ReviewRecipientRow[] =>
  changes.flatMap((item) =>
    item.removedEmails.map((email) => ({
      key: `${item.clientBillingProfileId}:${email.toLowerCase()}`,
      clientBillingProfileId: item.clientBillingProfileId,
      clientPublicKey: item.clientPublicKey,
      companyName: item.companyName,
      email,
    }))
  )

/**
 * Profiles whose outstanding rows cover every recipient they still have, so
 * deleting all of them would leave nobody to send the funding invoice to. This
 * happens when HubSpot returns no funding-report contacts for a company.
 * Keeping any one of the rows takes the profile back off this list.
 */
export const profilesLosingEveryRecipient = (
  rows: ReviewRecipientRow[],
  recipientsByProfileId: Record<number, string[]>
): Set<number> => {
  const droppedByProfile = new Map<number, Set<string>>()
  for (const row of rows) {
    const dropped =
      droppedByProfile.get(row.clientBillingProfileId) ?? new Set<string>()
    dropped.add(row.email.toLowerCase())
    droppedByProfile.set(row.clientBillingProfileId, dropped)
  }

  const clearing = new Set<number>()
  for (const [profileId, dropped] of Array.from(droppedByProfile.entries())) {
    const current = recipientsByProfileId[profileId] ?? []
    if (current.length === 0) continue
    const remaining = current.filter(
      (email) => !dropped.has(email.toLowerCase())
    )
    if (remaining.length === 0) clearing.add(profileId)
  }
  return clearing
}

export const buildReviewCompanyRows = (
  newItems: HubspotSyncNewItemDto[],
  warnings: HubspotSyncWarningItemDto[]
): ReviewCompanyRow[] => {
  const rows = [
    ...newItems
      .filter((item) => !isSubmittableNewItem(item))
      .map((item) => ({
        key: item.hubspotCompanyId,
        companyName: item.hubspotCompanyName,
        reason: 'No matching SureCo client, so no profile can be created',
      })),
    ...warnings.map((warning) => ({
      key: warning.hubspotCompanyId,
      companyName: warning.hubspotCompanyName,
      reason: warningReasonText(warning.reason),
    })),
  ]
  // Dedupe by company so a company can never render twice, and React keys stay
  // unique, even if a future backend change overlaps the two buckets.
  const byKey = new Map<string, ReviewCompanyRow>()
  for (const row of rows) if (!byKey.has(row.key)) byKey.set(row.key, row)
  return Array.from(byKey.values())
}

/** One new HubSpot company, with whatever the operator has filled in so far. */
export interface NewCompanyRow {
  item: HubspotSyncNewItemDto
  paymentType: string | undefined
  dueDay: number | undefined
  emails: string[]
  /** Addresses to also grant funding access on submit. */
  grantedEmails: string[]
  /**
   * Both a payment type and a due day are required: the design asks for them,
   * and `payment_type` is NOT NULL.
   */
  isReady: boolean
  /** Why the last submit refused this row; null until one does. */
  submitError: string | null
}

/** Stands in for a refused row the server gave no reason for. */
const SUBMIT_FAILURE_FALLBACK = 'Could not be saved'

/**
 * Why the server refused this submitted row, or null when it went through. A
 * row with no profile yet is matched on its client key, since the id it would
 * be reported under is the one the same request was meant to create.
 */
export const submitFailureReason = (
  failed: HubspotSyncSubmitFailureDto[],
  row: { clientBillingProfileId: number | null; clientPublicKey: string | null }
): string | null => {
  const match = failed.find((item) => {
    if (!isNil(row.clientBillingProfileId)) {
      return item.clientBillingProfileId === row.clientBillingProfileId
    }
    if (isNil(row.clientPublicKey)) return false
    return (
      isNil(item.clientBillingProfileId) &&
      item.clientPublicKey === row.clientPublicKey
    )
  })
  if (isNil(match)) return null
  const reason = match.reason.trim()
  return reason.length > 0 ? reason : SUBMIT_FAILURE_FALLBACK
}

export const buildCreateUpdates = (
  rows: NewCompanyRow[]
): HubspotSyncUpdateItemDto[] =>
  rows.map(({ item, paymentType, dueDay, emails, grantedEmails }) => ({
    clientBillingProfileId: null,
    clientPublicKey: item.clientPublicKey,
    toEmails: emails,
    accessEmails: grantedEmails,
    paymentType,
    dueDay: dueDay ?? null,
    planEffectiveDate: item.planEffectiveDate ?? undefined,
  }))

/**
 * Writes one matched profile's recipient list. `toEmails` is always the whole
 * intended list, so the Changes and Needs review modals can act on the same
 * profile in any order without one undoing the other. paymentType and
 * planEffectiveDate are sent only when they actually changed, because echoing an
 * unchanged legacy value would 400 the batch, and dueDay is omitted so an
 * existing one is never cleared.
 */
export const buildProfileUpdate = (
  profile: { clientBillingProfileId: number; clientPublicKey: string },
  toEmails: string[],
  {
    accessEmails,
    paymentType,
    planEffectiveDate,
  }: {
    /**
     * Cleared for portal access. Being invoiced is not access, so an empty list
     * grants nobody and omitting it leaves every written address eligible.
     */
    accessEmails?: string[]
    paymentType?: string
    planEffectiveDate?: string
  } = {}
): HubspotSyncUpdateItemDto => ({
  clientBillingProfileId: profile.clientBillingProfileId,
  clientPublicKey: profile.clientPublicKey,
  toEmails,
  accessEmails,
  paymentType,
  planEffectiveDate,
})

export interface SyncTask {
  key: SyncTaskKey
  label: string
  count: number
}

const plural = (count: number, one: string, many: string) =>
  count === 1 ? one : many

export const buildSyncTasks = ({
  newCount,
  changesCount,
  reviewCount,
  unsyncedCount,
}: {
  newCount: number
  changesCount: number
  reviewCount: number
  unsyncedCount: number
}): SyncTask[] =>
  [
    {
      key: 'new' as const,
      count: newCount,
      // Deliberately vague on which field is missing: the due day always is,
      // but the payment type arrives filled whenever the deal names one.
      label: `${newCount} new ${plural(newCount, 'company needs', 'companies need')} setting up`,
    },
    {
      key: 'changes' as const,
      count: changesCount,
      label: `${changesCount} existing ${plural(changesCount, 'company has', 'companies have')} recipient updates`,
    },
    {
      key: 'review' as const,
      count: reviewCount,
      label: `${reviewCount} ${plural(reviewCount, 'recipient needs', 'recipients need')} review`,
    },
    {
      // Nothing to decide, so it is listed apart from the review count that
      // deleting recipients drives down.
      key: 'unsynced' as const,
      count: unsyncedCount,
      label: `${unsyncedCount} HubSpot ${plural(unsyncedCount, 'company is', 'companies are')} not linked to a SureCo client`,
    },
  ].filter((task) => task.count > 0)

export const buildSyncHeading = (pendingCount: number): string =>
  pendingCount === 0
    ? 'Sync complete'
    : `Sync complete · ${pendingCount} ${plural(pendingCount, 'item needs', 'items need')} your review`

export const buildNewCompanyHint = ({
  total,
  readyCount,
}: {
  total: number
  readyCount: number
}): string => {
  if (total === 0) return 'Nothing left to set up.'
  const missing = total - readyCount
  if (missing === 0) return 'All companies are ready to submit.'
  return `${missing} ${plural(missing, 'company still needs', 'companies still need')} a payment type or due day.`
}
